import LessonRowChevronIcon from "@/assets/images/icons/lesson-row-chevron.svg";
import LessonRowListIcon from "@/assets/images/icons/lesson-row-list.svg";
import SidebarChevronActiveIcon from "@/assets/images/icons/sidebar-chevron-active.svg";
import SidebarChevronDisabledIcon from "@/assets/images/icons/sidebar-chevron-disabled.svg";
import LessonChapterScrollContainer from "@/components/lesson/LessonChapterScrollContainer";
import { LessonSidebarToggleButton } from "@/components/lesson/LessonSidebarToggleHandle";
import { LessonMetaLine } from "@/components/lesson/LessonStructuredTitleBlock";
import { CircularProgressIcon } from "@/components/ui/CircularProgressIcon";
import { LOADING_SPINNER_COLOR } from "@/lib/loadingSpinnerColor";
import { normalizeRouteQueryString } from "@/lib/lesson-deep-link";
import type {
  CourseMetadata,
  Lesson,
  LessonVideo,
  LessonVideoChapter,
  UserLessonVideoInteraction,
} from "@/types";
import { getLessonRowTitleParts } from "@/utils/lessonStructuredTitle";
import {
  getUserProgressForVideo,
  lessonProgressBarPercentFromVideos,
  sidebarVideoCirclePercent,
} from "@/utils/lessonVideoHelpers";
import { Ionicons } from "@expo/vector-icons";
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView as RNScrollView, View } from "react-native";
import { SizableText, XStack, YStack } from "tamagui";
import { BodyText, CardTitle, SubtitleText } from "@/components/ui/Typography";

const ACCENT = "#F2663A";
const BORDER = "#E0DED8";
export const SIDEBAR_HEADER_HEIGHT = 64;
export const SIDEBAR_SHELL_PADDING_TOP = 8;
const HEADER_DROP_SHADOW =
  Platform.OS === "web"
    ? ({ boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.08)", zIndex: 1 } as const)
    : ({
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 4,
        elevation: 2,
        zIndex: 1,
      } as const);

const COLLAPSED_LAYOUT = {
  sidebarPaddingTop: 8,
  sidebarPaddingBottom: 16,
  unitWidth: 48,
  unitMinHeight: 56,
  unitPaddingTop: 8,
  unitPaddingRight: 4,
  unitPaddingBottom: 8,
  unitPaddingLeft: 12,
  unitToProgressGap: 8,
  rowMarginTop: 7,
  rowPaddingBottom: 9,
  selectedBorderWidth: 6,
  selectedBorderColor: "#987451",
  selectedBackground: "#F4F1ED",
  progressBarHeight: 2,
  progressBarRadius: 30,
} as const;

export const LESSON_SIDEBAR_WIDTH = 320;
export const LESSON_SIDEBAR_WIDTH_LG = 365;
export const LESSON_SIDEBAR_COLLAPSED_WIDTH = 68;
export const LESSON_SIDEBAR_HEIGHT = 720;

const EXPANDED_ROW_PADDING_LEFT = 8;
const EXPANDED_ROW_STRIPE_GAP = 4;

function rowPaddingLeftWithAccent(basePaddingLeft: number, showAccent: boolean): number {
  if (showAccent) {
    return basePaddingLeft - COLLAPSED_LAYOUT.selectedBorderWidth;
  }
  return basePaddingLeft;
}

function collapsedLessonLabel(lessonNumber: number): string {
  return `L ${lessonNumber}`;
}

function collapsedUnitPaddingLeft(showSelectedAccent: boolean): number {
  return rowPaddingLeftWithAccent(COLLAPSED_LAYOUT.unitPaddingLeft, showSelectedAccent);
}

const CHAPTER_MAX_VISIBLE_ROWS = 5;
const CHAPTER_ROW_ESTIMATE_PX = 34;
const CHAPTER_LIST_VERTICAL_PADDING = 6;

const SIDEBAR_VIEWPORT_HEIGHT =
  LESSON_SIDEBAR_HEIGHT - SIDEBAR_HEADER_HEIGHT - SIDEBAR_SHELL_PADDING_TOP;

function chapterListHeight(numChapters: number): number {
  const visible = Math.min(numChapters, CHAPTER_MAX_VISIBLE_ROWS);
  return visible * CHAPTER_ROW_ESTIMATE_PX + CHAPTER_LIST_VERTICAL_PADDING;
}

type LessonRowWithProgress = Lesson & {
  user_lesson?: { progress?: number | null; is_completed?: boolean | null } | null;
  /** Companion (individually-sold) lesson the user doesn't own. */
  locked?: boolean;
  /** External checkout URL for locked companion lessons (web only). */
  buy_url?: string | null;
};

function videoCount(lesson: Lesson): number {
  const n = lesson.video_count;
  if (typeof n === "number" && n > 0) return n;
  return 1;
}

function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

function sortedChapters(chapters: LessonVideoChapter[]): LessonVideoChapter[] {
  return [...chapters].sort((a, b) => (a.begin ?? 0) - (b.begin ?? 0));
}

function videoHasChapters(video: LessonVideo): boolean {
  if (typeof video.no_of_chapters === "number") return video.no_of_chapters > 0;
  return Array.isArray(video.chapters) && video.chapters.length > 0;
}

function singleVideoLessonHasChapters(lesson: Lesson, rowVideos: LessonVideo[]): boolean {
  if (typeof lesson.total_chapters_count === "number") return lesson.total_chapters_count > 0;
  const firstVideo = rowVideos[0];
  return firstVideo ? videoHasChapters(firstVideo) : false;
}

function activeChapterIndexFromTime(chapters: LessonVideoChapter[], t: number): number {
  const sorted = sortedChapters(chapters);
  if (sorted.length === 0) return 0;
  let idx = 0;
  for (let i = 0; i < sorted.length; i++) {
    if ((sorted[i].begin ?? 0) <= t) idx = i;
    else break;
  }
  return idx;
}

type Props = {
  loading: boolean;
  lessons: Lesson[];
  activeLessonSlug: string;
  currentVideoId: number | null;
  currentTimeSeconds: number;
  lessonVideosByLessonId: Record<number, LessonVideo[]>;
  userVideosByLessonId: Record<number, UserLessonVideoInteraction[]>;
  siblingLoadingLessonIds?: number[];
  onSiblingExpand?: (lessonId: number) => void;
  onSelectLesson: (lessonSlug: string) => void;
  onSelectVideo: (video: LessonVideo) => void;
  onChapterSeek: (seconds: number) => void;
  onChapterLinkClick?: (
    lessonSlug: string,
    video: LessonVideo,
    chapter: LessonVideoChapter,
  ) => void;
  onSelectLessonAtVideo?: (lessonSlug: string, video: LessonVideo) => void;
  onSelectLessonAtChapter?: (lessonSlug: string, video: LessonVideo, chapter: LessonVideoChapter) => void;
  headerTitle?: string | null;
  seasonTitle?: string | null;
  onSeasonPrev?: () => void;
  onSeasonNext?: () => void;
  canPrevSeason?: boolean;
  canNextSeason?: boolean;
  fillParent?: boolean;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  courseMetadata?: CourseMetadata | null;
  ScrollViewComponent?: typeof RNScrollView;
};

function LessonCourseSidebarImpl({
  loading,
  lessons,
  activeLessonSlug,
  currentVideoId,
  currentTimeSeconds,
  lessonVideosByLessonId,
  userVideosByLessonId,
  siblingLoadingLessonIds = [],
  onSiblingExpand,
  onSelectLesson,
  onSelectVideo,
  onChapterSeek,
  onChapterLinkClick,
  onSelectLessonAtVideo,
  onSelectLessonAtChapter,
  headerTitle,
  seasonTitle,
  onSeasonPrev,
  onSeasonNext,
  canPrevSeason = false,
  canNextSeason = false,
  fillParent = false,
  collapsed = false,
  onToggleCollapsed,
  courseMetadata = null,
  ScrollViewComponent = RNScrollView,
}: Props) {
  const [activeExpanded, setActiveExpanded] = useState(false);
  const [siblingExpandedSlug, setSiblingExpandedSlug] = useState<string | null>(null);
  const [activeChapterVideoId, setActiveChapterVideoId] = useState<number | null>(null);
  const [siblingChapterVideoId, setSiblingChapterVideoId] = useState<number | null>(null);
  const [hoveredLessonSlug, setHoveredLessonSlug] = useState<string | null>(null);

  const prevSlugRef = useRef<string | null>(null);
  const forceExpandOnNextActiveSlugRef = useRef<string | null>(null);
  const forceOpenChaptersVideoIdOnNextActiveSlugRef = useRef<number | null>(null);
  const listScrollRef = useRef<RNScrollView>(null);
  const hasScrolledOnMountRef = useRef(false);
  const scrollOffsetRef = useRef(0);
  const pendingExpandScrollSlugRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const n = normalizeRouteQueryString(activeLessonSlug);
    if (prevSlugRef.current === n) return;
    prevSlugRef.current = n;
    hasScrolledOnMountRef.current = false;

    const row = lessons.find((l) => normalizeRouteQueryString(l.slug) === n);
    const shouldForceExpand = forceExpandOnNextActiveSlugRef.current === n;
    forceExpandOnNextActiveSlugRef.current = null;
    const forcedChaptersVideoId = forceOpenChaptersVideoIdOnNextActiveSlugRef.current;
    forceOpenChaptersVideoIdOnNextActiveSlugRef.current = null;

    const canApplyNextState = row != null || shouldForceExpand || forcedChaptersVideoId != null;
    if (!canApplyNextState) return;

    setSiblingExpandedSlug(null);
    setSiblingChapterVideoId(null);

    setActiveExpanded(
      shouldForceExpand ||
        forcedChaptersVideoId != null ||
        (row != null && videoCount(row) > 1),
    );
    if (forcedChaptersVideoId != null) setActiveChapterVideoId(forcedChaptersVideoId);
    else setActiveChapterVideoId(null);
  }, [activeLessonSlug, lessons]);

  const toggleChaptersForVideo = useCallback((videoId: number, isActiveLessonRow: boolean) => {
    if (isActiveLessonRow) {
      setActiveChapterVideoId((prev) => {
        const opening = prev !== videoId;
        if (opening) pendingExpandScrollSlugRef.current = activeLessonSlug ?? null;
        return opening ? videoId : null;
      });
    } else {
      setSiblingChapterVideoId((prev) => (prev === videoId ? null : videoId));
    }
  }, []);

  const activeLessonSlugNorm = useMemo(
    () => normalizeRouteQueryString(activeLessonSlug),
    [activeLessonSlug],
  );

  const sortedLessonVideos = useMemo(() => {
    const result: Record<number, LessonVideo[]> = {};
    for (const [id, videos] of Object.entries(lessonVideosByLessonId)) {
      result[Number(id)] = [...videos].sort((a, b) => (a.order_no ?? 0) - (b.order_no ?? 0));
    }
    return result;
  }, [lessonVideosByLessonId]);

  const showSeasonNav = Boolean(seasonTitle) && Boolean(onSeasonPrev || onSeasonNext);
  const title = headerTitle ?? seasonTitle ?? null;
  const hideLessonNumber = courseMetadata?.hide_lesson_number === true;

  const showHeaderDropShadow = fillParent;  const headerBlock = title || onToggleCollapsed || showSeasonNav ? (
    <XStack
      ai="center"
      jc={collapsed ? "center" : "space-between"}
      gap={collapsed ? 0 : 8}
      height={SIDEBAR_HEADER_HEIGHT}
      px={collapsed ? 0 : fillParent ? undefined : 14}
      pl={collapsed ? 0 : fillParent ? 16 : undefined}
      pr={collapsed ? 0 : fillParent ? 8 : undefined}
      borderBottomWidth={showHeaderDropShadow ? 0 : 1}
      borderBottomColor={BORDER}
      bg="#FFFFFF"
      flexShrink={0}
      style={showHeaderDropShadow ? HEADER_DROP_SHADOW : undefined}
    >
      {collapsed && onToggleCollapsed ? (
        <LessonSidebarToggleButton
          expanded={!collapsed}
          onPress={onToggleCollapsed}
          accessibilityLabel="Expand course lessons"
        />
      ) : (
        <>
          {showSeasonNav ? (
            <Pressable
              onPress={canPrevSeason ? onSeasonPrev : undefined}
              disabled={!canPrevSeason}
              style={{
                width: 40,
                height: 40,
                padding: 12,
                borderRadius: 30,
                borderWidth: 1,
                borderColor: "#C9C5C3",
                backgroundColor: "#FFF",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <View style={{ transform: [{ rotate: "180deg" }] }}>
                {canPrevSeason ? (
                  <SidebarChevronActiveIcon width={8} height={13} />
                ) : (
                  <SidebarChevronDisabledIcon width={8} height={13} />
                )}
              </View>
            </Pressable>
          ) : (
            <View style={{ width: onToggleCollapsed ? 40 : 0, flexShrink: 0 }} />
          )}

          {title ? (
            <SubtitleText
              color="#5C5854"
              letterSpacing={0}
              numberOfLines={1}
              flex={1}
              textAlign="center"
              minWidth={0}
            >
              {title}
            </SubtitleText>
          ) : (
            <View style={{ flex: 1 }} />
          )}

          <XStack ai="center" gap={12} flexShrink={0}>
            {showSeasonNav ? (
              <Pressable
                onPress={canNextSeason ? onSeasonNext : undefined}
                disabled={!canNextSeason}
                style={{
                  width: 40,
                  height: 40,
                  padding: 12,
                  borderRadius: 30,
                  borderWidth: 1,
                  borderColor: "#C9C5C3",
                  backgroundColor: "#FFF",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {canNextSeason ? (
                  <SidebarChevronActiveIcon width={8} height={13} />
                ) : (
                  <SidebarChevronDisabledIcon width={8} height={13} />
                )}
              </Pressable>
            ) : null}

            {onToggleCollapsed ? (
              <LessonSidebarToggleButton
                expanded={!collapsed}
                onPress={onToggleCollapsed}
                accessibilityLabel="Collapse course lessons"
              />
            ) : null}
          </XStack>
        </>
      )}
    </XStack>
  ) : null;

  const shellStyle = fillParent
    ? undefined
    : ({ boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.08), 0 0 4px 0 rgba(0, 0, 0, 0.06)" } as const);

  const listBody =
    loading ? (
      <YStack f={1} ai="center" jc="center" minHeight={200}>
        <ActivityIndicator size="small" color={LOADING_SPINNER_COLOR} />
      </YStack>
    ) : lessons.length === 0 ? (
      <YStack f={1} ai="center" jc="center" px="$3" minHeight={200}>
        <BodyText color="#5C5854" ta="center">
          No lessons in this course.
        </BodyText>
      </YStack>
    ) : (
      <ScrollViewComponent
         ref={listScrollRef}
         style={{ flex: 1, width: "100%" }}
         contentContainerStyle={{
           paddingHorizontal: collapsed ? 0 : 16,
           paddingTop: 0,
           paddingBottom: collapsed ? COLLAPSED_LAYOUT.sidebarPaddingBottom : 0,
           alignItems: collapsed ? "center" : undefined,
         }}
         showsVerticalScrollIndicator={!collapsed}
         scrollIndicatorInsets={{ right: 0 }}
         nestedScrollEnabled
         keyboardShouldPersistTaps="handled"
         onScroll={(e: any) => { scrollOffsetRef.current = e.nativeEvent.contentOffset.y; }}
         scrollEventThrottle={16}
       >
        <YStack ai={collapsed ? "center" : undefined} w="100%">
          {lessons.map((lesson: LessonRowWithProgress, index) => {
            const lessonSlugNorm = normalizeRouteQueryString(lesson.slug);
            const isActiveLesson = lessonSlugNorm === activeLessonSlugNorm;
            const multi = videoCount(lesson) > 1;
            const lessonNumber = index + 1;
            const isHovered =
              hoveredLessonSlug != null &&
              normalizeRouteQueryString(hoveredLessonSlug) === lessonSlugNorm;

            const isLessonExpanded = isActiveLesson
              ? activeExpanded
              : siblingExpandedSlug != null &&
                normalizeRouteQueryString(siblingExpandedSlug) === lessonSlugNorm;

            const rowVideos = sortedLessonVideos[lesson.id] ?? [];
            const rowUserVideos = userVideosByLessonId[lesson.id] ?? [];
            const isSiblingLoading = siblingLoadingLessonIds.includes(lesson.id);
            const showVideos = isLessonExpanded && (isSiblingLoading || rowVideos.length > 0);
            const singleVideoHasChapters = !multi && singleVideoLessonHasChapters(lesson, rowVideos);
            const { displayTitle, metaLine, metaPrefix, metaSuffix } = getLessonRowTitleParts(
              lesson,
              {
                lessonTitleStyle: courseMetadata?.lesson_title_style,
                lessonNumber,
                weekFormat: "long",
              },
            );

            const onChevronPress = () => {
              if (isActiveLesson) {
                setActiveExpanded((prev) => {
                  if (!prev) pendingExpandScrollSlugRef.current = lesson.slug ?? null;
                  return !prev;
                });
              } else {
                setSiblingExpandedSlug((prev) => {
                  const alreadyOpen =
                    prev != null && normalizeRouteQueryString(prev) === lessonSlugNorm;
                  if (alreadyOpen) {
                    setSiblingChapterVideoId(null);
                    return null;
                  }
                  pendingExpandScrollSlugRef.current = lesson.slug ?? null;
                  setSiblingChapterVideoId(null);
                  if (onSiblingExpand) onSiblingExpand(lesson.id);
                  return lesson.slug;
                });
              }
            };

            const isLocked = Boolean(lesson.locked);
            const lessonBuyUrl = lesson.buy_url ?? null;

            const onLessonRowPress = () => {
              // Locked companion lesson: on web, go buy it (external checkout
              // with the lesson in the cart). On native, fall through to the
              // normal navigation — the lesson page shows the paywall (App
              // Store 3.1.1 forbids external purchase links).
              if (
                isLocked &&
                lessonBuyUrl &&
                Platform.OS === "web" &&
                typeof window !== "undefined"
              ) {
                window.location.href = lessonBuyUrl;
                return;
              }
              if (collapsed || !isActiveLesson) onSelectLesson(lesson.slug);
            };

            const progressPct = lessonProgressBarPercentFromVideos(
              rowVideos,
              rowUserVideos,
              lesson.user_lesson?.progress ?? null,
            );

            return (
              <YStack
                key={String(lesson.id)}
                w={collapsed ? "100%" : undefined}
                ai={collapsed ? "center" : undefined}
                opacity={isLocked ? 0.55 : 1}
                onLayout={(e) => {
                  const { y, height } = e.nativeEvent.layout;
                  if (isActiveLesson && !hasScrolledOnMountRef.current) {
                    hasScrolledOnMountRef.current = true;
                    requestAnimationFrame(() => {
                      listScrollRef.current?.scrollTo({
                        y: Math.max(0, y - SIDEBAR_VIEWPORT_HEIGHT / 2),
                        animated: false,
                      });
                    });
                    return;
                  }
                  if (
                    pendingExpandScrollSlugRef.current != null &&
                    normalizeRouteQueryString(pendingExpandScrollSlugRef.current) === lessonSlugNorm
                  ) {
                    pendingExpandScrollSlugRef.current = null;
                    const bottom = y + height;
                    const visibleBottom = scrollOffsetRef.current + SIDEBAR_VIEWPORT_HEIGHT;
                    if (bottom > visibleBottom) {
                      listScrollRef.current?.scrollTo({
                        y: Math.max(0, bottom - SIDEBAR_VIEWPORT_HEIGHT + 16),
                        animated: true,
                      });
                    }
                  }
                }}
              >
                {collapsed ? (
                  <View
                    style={{
                      width: "100%",
                      alignItems: "center",
                      marginTop: COLLAPSED_LAYOUT.rowMarginTop,
                    }}
                  >
                    <View
                      style={{
                        height: 78,
                        width: "100%",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Pressable
                        onPress={onLessonRowPress}
                        accessibilityRole="button"
                        onHoverIn={() => setHoveredLessonSlug(lesson.slug)}
                        onHoverOut={() => {
                          setHoveredLessonSlug((prev) =>
                            prev != null && normalizeRouteQueryString(prev) === lessonSlugNorm
                              ? null
                              : prev,
                          );
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        {({ pressed }) => {
                          const showCollapsedSelected =
                            isActiveLesson || isHovered || pressed;
                          return (
                          <View
                            style={{
                              minWidth: COLLAPSED_LAYOUT.unitWidth,
                              minHeight: COLLAPSED_LAYOUT.unitMinHeight,
                              flexDirection: "column",
                              alignItems: "stretch",
                              justifyContent: "center",
                              gap: COLLAPSED_LAYOUT.unitToProgressGap,
                              paddingTop: COLLAPSED_LAYOUT.unitPaddingTop,
                              paddingRight: COLLAPSED_LAYOUT.unitPaddingRight,
                              paddingBottom: COLLAPSED_LAYOUT.unitPaddingBottom,
                              paddingLeft: collapsedUnitPaddingLeft(showCollapsedSelected),
                              borderRadius: 4,
                              borderLeftWidth: showCollapsedSelected
                                ? COLLAPSED_LAYOUT.selectedBorderWidth
                                : 0,
                              borderLeftColor: COLLAPSED_LAYOUT.selectedBorderColor,
                              backgroundColor: showCollapsedSelected
                                ? COLLAPSED_LAYOUT.selectedBackground
                                : "transparent",
                              ...(Platform.OS === "web"
                                ? ({ boxSizing: "border-box" } as const)
                                : null),
                            }}
                          >
                            <SizableText
                              fontSize={13}
                              fontWeight="600"
                              color="#0D0D0D"
                              lineHeight={18}
                              textAlign="center"
                            >
                              {hideLessonNumber ? "L" : collapsedLessonLabel(lessonNumber)}
                            </SizableText>
                            <View
                              style={{
                                alignSelf: "stretch",
                                height: COLLAPSED_LAYOUT.progressBarHeight,
                                borderRadius: COLLAPSED_LAYOUT.progressBarRadius,
                                backgroundColor: "rgba(0,0,0,0.08)",
                                overflow: "hidden",
                              }}
                            >
                              <View
                                style={{
                                  height: COLLAPSED_LAYOUT.progressBarHeight,
                                  borderRadius: COLLAPSED_LAYOUT.progressBarRadius,
                                  backgroundColor: ACCENT,
                                  width: `${progressPct}%`,
                                }}
                              />
                            </View>
                          </View>
                        );
                        }}
                      </Pressable>
                    </View>
                    <View
                      style={{
                        width: COLLAPSED_LAYOUT.unitWidth,
                        height: 1,
                        marginTop: COLLAPSED_LAYOUT.rowPaddingBottom,
                        backgroundColor: BORDER,
                      }}
                    />
                  </View>
                ) : (
                <Pressable
                  onPress={onLessonRowPress}
                  style={({ pressed }) => {
                    const showRowAccent =
                      isActiveLesson || isHovered || (!isActiveLesson && pressed);
                    return {
                          gap: 5,
                          alignItems: "flex-start",
                          justifyContent: "flex-start",
                          backgroundColor:
                            isHovered || (!isActiveLesson && pressed)
                              ? COLLAPSED_LAYOUT.selectedBackground
                              : isActiveLesson && multi && currentVideoId != null
                                ? "transparent"
                                : isActiveLesson
                                  ? COLLAPSED_LAYOUT.selectedBackground
                                  : "transparent",
                          borderLeftWidth: COLLAPSED_LAYOUT.selectedBorderWidth,
                          borderLeftColor: showRowAccent
                            ? COLLAPSED_LAYOUT.selectedBorderColor
                            : "transparent",
                          borderTopLeftRadius: 4,
                          borderTopRightRadius: 4,
                          borderBottomLeftRadius: isLessonExpanded ? 0 : 4,
                          borderBottomRightRadius: isLessonExpanded ? 0 : 4,
                          minHeight: 78,
                          paddingTop: 8,
                          paddingBottom: 10,
                          paddingLeft: EXPANDED_ROW_STRIPE_GAP + EXPANDED_ROW_PADDING_LEFT,
                          paddingRight: EXPANDED_ROW_PADDING_LEFT,
                          alignSelf: "stretch",
                          marginTop: COLLAPSED_LAYOUT.rowMarginTop,
                          cursor: "pointer",
                          position: "relative",
                          ...(Platform.OS === "web"
                            ? ({ boxSizing: "border-box" } as const)
                            : null),
                        };
                  }}
                  onHoverIn={() => setHoveredLessonSlug(lesson.slug)}
                  onHoverOut={() => {
                    setHoveredLessonSlug((prev) =>
                      prev != null && normalizeRouteQueryString(prev) === lessonSlugNorm ? null : prev,
                    );
                  }}
                >
                  <>
                  {metaLine ? (
                    hideLessonNumber ? (
                      // Reserve the meta-line height so hiding the number does
                      // not shift the title up / change row height.
                      <View style={{ height: 20 }} />
                    ) : (
                      <LessonMetaLine
                        metaLine={metaLine}
                        metaPrefix={metaPrefix}
                        metaSuffix={metaSuffix}
                        metaFontSize={14}
                        metaLineHeight={20}
                      />
                    )
                  ) : null}

                  <XStack ai="center" jc="space-between" gap={8} alignSelf="stretch">
                      {isLocked ? (
                        <Ionicons
                          name="lock-closed"
                          size={13}
                          color="#5C5854"
                          style={{ flexShrink: 0 }}
                        />
                      ) : null}
                      <CardTitle
                        fontWeight="600"
                        color="#0D0D0D"
                        letterSpacing={0}
                        flex={1}
                      >
                        {displayTitle}
                      </CardTitle>

                    {!isLocked && (multi || singleVideoHasChapters) && (
                      <Pressable
                        onPress={onChevronPress}
                        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        style={({ pressed }) => ({
                          flexShrink: 0,
                          backgroundColor: pressed ? "rgba(0,0,0,0.05)" : "transparent",
                          borderRadius: 8,
                        })}
                      >
                        {multi ? (
                          <View style={{ transform: [{ rotate: isLessonExpanded ? "180deg" : "0deg" }] }}>
                            <LessonRowChevronIcon width={24} height={24} />
                          </View>
                        ) : (
                          <View style={{ width: 24, height: 24, alignItems: "center", justifyContent: "center" }}>
                            <LessonRowListIcon width={14} height={10} />
                          </View>
                        )}
                      </Pressable>
                    )}
                  </XStack>

                  <View
                    style={{
                      height: 3,
                      borderRadius: 2,
                      backgroundColor: "rgba(0,0,0,0.08)",
                      alignSelf: "stretch",
                      maxWidth: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <View
                      style={{
                        height: 3,
                        borderRadius: 2,
                        backgroundColor: ACCENT,
                        width: `${progressPct}%`,
                      }}
                    />
                  </View>
                    </>
                </Pressable>
                )}

                {!collapsed && showVideos ? (
                  isSiblingLoading && rowVideos.length === 0 ? (
                    <YStack py="$3" pl={22} ai="flex-start">
                      <ActivityIndicator size="small" color={LOADING_SPINNER_COLOR} />
                    </YStack>
                  ) : (
                    rowVideos.map((video: LessonVideo) => {
                      const isActiveVideo = isActiveLesson && currentVideoId === video.id;
                      const progress = getUserProgressForVideo(video.id, rowUserVideos);
                      const done = progress?.is_completed || false;
                      const pct = sidebarVideoCirclePercent(video, progress);
                      const chapters = Array.isArray(video.chapters) ? sortedChapters(video.chapters) : [];
                      const hasVideoChapters = videoHasChapters(video);
                      const chaptersOpen = !multi
                        ? isLessonExpanded
                        : isActiveLesson
                        ? activeChapterVideoId === video.id
                        : siblingChapterVideoId === video.id;
                      const activeChIdx =
                        isActiveVideo && chapters.length > 0
                          ? activeChapterIndexFromTime(chapters, currentTimeSeconds)
                          : -1;

                      const isFirstVideo = rowVideos.indexOf(video) === 0;

                      return (
                        <YStack key={video.id} marginBottom={8} marginTop={isFirstVideo ? 8 : 0}>
                          {multi && (
                          <XStack
                            ai="center"
                            px={8}
                            py={10}
                            gap={10}
                            backgroundColor={isActiveVideo ? "#F4F1ED" : "#FFFFFF"}
                            borderRadius={4}
                          >
                            <Pressable
                              onPress={() => {
                                if (isActiveLesson) {
                                  if (multi || activeExpanded) setActiveExpanded(true);
                                  onSelectVideo(video);
                                } else if (onSelectLessonAtVideo) {
                                  forceExpandOnNextActiveSlugRef.current = lessonSlugNorm;
                                  onSelectLessonAtVideo(lesson.slug, video);
                                } else {
                                  onSelectLesson(lesson.slug);
                                }
                              }}
                              style={{ flex: 1, flexDirection: "row", alignItems: "center", gap: 10 }}
                            >
                              {done ? (
                                <View
                                  style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: 12,
                                    backgroundColor: ACCENT,
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  <Ionicons name="checkmark" size={14} color="#FFFFFF" />
                                </View>
                              ) : (
                                <CircularProgressIcon
                                  progress={pct}
                                  size={24}
                                  strokeWidth={2}
                                  showPlayIcon={false}
                                  iconColor="#1B1B1B"
                                  progressColor={ACCENT}
                                  backgroundColor="#1B1B1B"
                                />
                              )}
                              <YStack f={1} minWidth={0} gap={2}>
                                <CardTitle fontWeight="600" color="#0D0D0D" numberOfLines={1}>
                                  {video.title}
                                </CardTitle>
                                {video.duration ? (
                                  <SizableText fontSize={13} lineHeight={18} color="#5C5854" fontWeight="400">
                                    {formatClock(video.duration)}
                                  </SizableText>
                                ) : null}
                              </YStack>
                            </Pressable>

                            {hasVideoChapters ? (
                              <Pressable
                                onPress={() => toggleChaptersForVideo(video.id, isActiveLesson)}
                                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                                style={{ flexShrink: 0 }}
                                accessibilityLabel={chaptersOpen ? "Hide chapters" : "Show chapters"}
                              >
                                <View style={{ width: 24, height: 24, alignItems: "center", justifyContent: "center" }}>
                                  <LessonRowListIcon width={14} height={10} />
                                </View>
                              </Pressable>
                            ) : (
                              <View style={{ width: 24 }} />
                            )}
                          </XStack>
                          )}

                          {chaptersOpen && chapters.length > 0 ? (
                            <View style={{ paddingTop: 8, paddingBottom: 8, position: "relative" }}>
                              <View
                                pointerEvents="none"
                                style={{
                                  position: "absolute",
                                  left: 44,
                                  top: 0,
                                  bottom: 0,
                                  width: 1,
                                  backgroundColor: "rgba(0,0,0,0.12)",
                                }}
                              />
                              <LessonChapterScrollContainer
                                height={chapterListHeight(chapters.length)}
                                chapterRowHeightEstimate={CHAPTER_ROW_ESTIMATE_PX}
                                scrollLayoutKey={
                                  chaptersOpen ? `${video.id}-open-${chapters.length}` : `${video.id}-closed`
                                }
                                scrollActiveChapterIndex={
                                  chaptersOpen && isActiveVideo && activeChIdx >= 0 ? activeChIdx : null
                                }
                              >
                                {chapters.map((ch, ci) => {
                                  const hasPlaybackFocus = isActiveVideo && activeChIdx >= 0;
                                  const isActiveChapter =
                                    hasPlaybackFocus && activeChIdx >= 0 && ci === activeChIdx;
                                  return (
                                    <Pressable
                                      key={String(ch.id)}
                                      onPress={() => {
                                        onChapterLinkClick?.(lesson.slug, video, ch);
                                        if (isActiveVideo) {
                                          onChapterSeek(ch.begin ?? 0);
                                        } else if (onSelectLessonAtChapter) {
                                          forceExpandOnNextActiveSlugRef.current = lessonSlugNorm;
                                          forceOpenChaptersVideoIdOnNextActiveSlugRef.current = video.id;
                                          onSelectLessonAtChapter(lesson.slug, video, ch);
                                        } else {
                                          onSelectLesson(lesson.slug);
                                        }
                                      }}
                                      style={{ backgroundColor: "transparent" }}
                                    >
                                      <XStack ai="flex-start" py={7} pr={8}>
                                        <SizableText
                                          size="$2"
                                          fontWeight={isActiveChapter ? "700" : "400"}
                                          color={isActiveChapter ? "#3F3A36" : "#B0ACA8"}
                                          style={{
                                            width: 40,
                                            textAlign: "center",
                                            flexShrink: 0,
                                          }}
                                        >
                                          {formatClock(ch.begin ?? 0)}
                                        </SizableText>
                                        <SizableText
                                          size="$2"
                                          fontWeight={isActiveChapter ? "700" : "500"}
                                          color={isActiveChapter ? "#3F3A36" : "#5C5854"}
                                          flex={1}
                                          flexShrink={1}
                                          pl={12}
                                        >
                                          {ch.title}
                                        </SizableText>
                                      </XStack>
                                    </Pressable>
                                  );
                                })}
                              </LessonChapterScrollContainer>
                            </View>
                          ) : null}
                        </YStack>
                      );
                    })
                  )
                ) : null}

                {!collapsed ? (
                  <View
                    style={{
                      height: 1,
                      backgroundColor: BORDER,
                      marginTop: COLLAPSED_LAYOUT.rowPaddingBottom,
                    }}
                  />
                ) : null}
              </YStack>
            );
          })}
        </YStack>
      </ScrollViewComponent>
    );

  return (
    <YStack
      width={fillParent ? "100%" : LESSON_SIDEBAR_WIDTH}
      height={fillParent ? "100%" : LESSON_SIDEBAR_HEIGHT}
      flex={fillParent ? 1 : undefined}
      flexShrink={fillParent ? undefined : 0}
      bg="$white"
      br={fillParent ? 0 : "$4"}
      borderWidth={fillParent ? 0 : 1}
      borderColor={BORDER}
      overflow="hidden"
      pt={fillParent ? SIDEBAR_SHELL_PADDING_TOP : 0}
      style={shellStyle}
    >
      <YStack f={1} minHeight={0} overflow="hidden">
        {headerBlock}
        <YStack
          f={1}
          minHeight={0}
          pt={fillParent || collapsed ? 0 : 12}
          pb={fillParent ? 0 : 16}
          ai={fillParent ? "stretch" : "center"}
        >
          {listBody}
        </YStack>
      </YStack>
    </YStack>
  );
}

export const LessonCourseSidebar = React.memo(LessonCourseSidebarImpl);
